import type { DraggableData, DraggableEvent } from 'react-draggable'
import type { ReactNode, RefObject } from 'react'
import { debounce, uniqueId } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import Draggable from 'react-draggable'

interface CrossedBorders {
  top: boolean
  bottom: boolean
  left: boolean
  right: boolean
}

interface BorderValues {
  elementMaxTop: number
  elementMinTop: number
  elementMaxLeftShift: number
  elementMaxRightShift: number
}

interface ControlledPosition {
  x: number
  y: number
}

interface DraggableSettingsLayerWrapperProps {
  readonly children: ReactNode
  readonly dragAnchorRef: RefObject<HTMLElement | null>
}

export function hasCrossedBounds(
  bounds: DOMRect,
  borders: BorderValues,
  options: { checkBottom?: boolean } = { checkBottom: true },
): CrossedBorders | false {
  const isTopCrossed = bounds.top < borders.elementMinTop
  const isBottomCrossed = options.checkBottom ? bounds.bottom > window.innerHeight : false
  const isLeftCrossed = bounds.left < borders.elementMaxLeftShift

  // Only check right boundary if element is smaller than viewport
  const isRightCrossed = bounds.width < window.innerWidth ? bounds.right > borders.elementMaxRightShift : false

  if (isTopCrossed || isBottomCrossed || isLeftCrossed || isRightCrossed) {
    return { top: isTopCrossed, bottom: isBottomCrossed, left: isLeftCrossed, right: isRightCrossed }
  }
  return false
}

export function getBorderValues(draggable: HTMLElement, anchor: HTMLElement): BorderValues {
  const { innerHeight, innerWidth } = window

  // using anchor.offsetTop & anchor.offsetLeft because we know
  // that position for child in this case is absolute
  const anchorTopOffset = anchor.offsetTop + anchor.clientHeight
  const anchorRightOffset = draggable.clientWidth - (anchor.offsetLeft + anchor.clientWidth)

  return {
    elementMaxTop: innerHeight - anchorTopOffset,
    elementMinTop: -anchor.offsetTop,
    elementMaxLeftShift: -anchor.offsetLeft,
    elementMaxRightShift: innerWidth + anchorRightOffset,
  }
}

export function calculateShift(
  bounds: DOMRect,
  crossed: CrossedBorders,
  borders: BorderValues,
): { x: number; y: number } {
  // Calculate horizontal shift
  let x = 0
  if (crossed.right && bounds.width < window.innerWidth) {
    x = borders.elementMaxRightShift - bounds.right
  } else if (crossed.left) {
    x = borders.elementMaxLeftShift - bounds.left
  }

  // Calculate vertical shift
  let y = 0
  if (crossed.top) {
    y = borders.elementMinTop - bounds.top
  } else if (crossed.bottom) {
    y = window.innerHeight - bounds.bottom
  }

  return { x, y }
}

export function DraggableSettingsLayerWrapper({ children, dragAnchorRef }: DraggableSettingsLayerWrapperProps) {
  const [borderValues, setBorderValues] = useState<BorderValues | null>(null)
  const [controlledPosition, setControlledPosition] = useState<ControlledPosition>({ x: 0, y: 0 })

  const elementRef = useRef<HTMLDivElement>(null)

  const initBorders = useCallback(
    (node: HTMLElement | null, callback?: (node: HTMLElement) => void) => {
      if (!node || !dragAnchorRef.current) return
      const newBorders = getBorderValues(node, dragAnchorRef.current)
      setBorderValues(newBorders)
      callback?.(node)
    },
    [dragAnchorRef],
  )

  const handleElementPosition = useCallback(() => {
    if (!elementRef.current || !dragAnchorRef.current || !borderValues) return

    const bounds = elementRef.current.getBoundingClientRect()
    const crossed = hasCrossedBounds(bounds, borderValues, { checkBottom: true })

    if (crossed) {
      const { x, y } = calculateShift(bounds, crossed, borderValues)
      setControlledPosition((prev) => ({ x: prev.x + x, y: prev.y + y }))
    }
  }, [borderValues, dragAnchorRef])

  const onStart = useCallback((_: DraggableEvent, pos: DraggableData) => initBorders(pos.node), [initBorders])

  const onStop = useCallback(
    (_: DraggableEvent | null, pos: DraggableData) => {
      if (!pos.node || !borderValues) return
      const bounds = pos.node.getBoundingClientRect()
      const crossed = hasCrossedBounds(bounds, borderValues, { checkBottom: false })

      if (crossed) {
        const { x, y } = calculateShift(bounds, crossed, borderValues)
        setControlledPosition({
          x: pos.x + (pos.deltaX || 0) + x,
          y: pos.y + (pos.deltaY || 0) + y,
        })
      }
    },
    [borderValues],
  )

  const onDrag = useCallback(
    (_: DraggableEvent, position: DraggableData) => setControlledPosition({ x: position.x, y: position.y }),
    [],
  )

  useEffect(() => {
    const handleResize = debounce(
      () => {
        initBorders(elementRef.current, (node) => {
          onStop(null, {
            node,
            x: controlledPosition.x,
            y: controlledPosition.y,
            deltaX: 0,
            deltaY: 0,
            lastX: controlledPosition.x,
            lastY: controlledPosition.y,
          })
        })
      },
      200,
      { leading: false, trailing: true },
    )

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [initBorders, onStop, controlledPosition])

  // Adjust position when element size changes (if some event triggers a resize and window goes out of bounds)
  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleElementPosition)
    const draggableElement = elementRef.current

    if (draggableElement) {
      resizeObserver.observe(draggableElement)
    }

    return () => {
      if (draggableElement) resizeObserver.unobserve(draggableElement)
    }
  }, [handleElementPosition])

  useEffect(() => {
    if (dragAnchorRef.current) {
      dragAnchorRef.current.id ||= uniqueId('drag-anchor-')
    }
  }, [dragAnchorRef])

  // Initial positioning logic to ensure element starts in a valid position
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (elementRef.current && dragAnchorRef.current) {
        const bounds = elementRef.current.getBoundingClientRect()
        const newBorders = getBorderValues(elementRef.current, dragAnchorRef.current)
        setBorderValues(newBorders)

        // Adjust if bottom is out of view
        if (bounds.bottom > window.innerHeight) {
          setControlledPosition({ x: 0, y: window.innerHeight - bounds.bottom })
        } else {
          const crossed = hasCrossedBounds(bounds, newBorders)
          if (crossed) {
            const { x, y } = calculateShift(bounds, crossed, newBorders)
            setControlledPosition({ x: x, y: y })
          }
        }
      }
    }, 10) // Small wait to ensure element is properly positioned
    return () => clearTimeout(timeoutId)
  }, [dragAnchorRef])

  return (
    <Draggable
      handle={dragAnchorRef.current?.id ? `#${dragAnchorRef.current.id}` : undefined}
      position={controlledPosition}
      onStart={onStart}
      onStop={onStop}
      onDrag={onDrag}
    >
      <div ref={elementRef}>{children}</div>
    </Draggable>
  )
}

export default DraggableSettingsLayerWrapper
